import React from 'react';
import { Star, Calculator, FileText, TrendingUp, Shield, Globe2, Building2, FileCheck2, CheckCircle2 } from 'lucide-react';
import { motion } from "framer-motion";

export default function UAEPage() {

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-900 via-blue-700 to-cyan-500 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        {/* Decorative Icons */}
        <div className="absolute top-20 left-10 text-green-400 opacity-20">
          <Calculator size={40} />
        </div>
        <div className="absolute top-40 right-20 text-yellow-300 opacity-20">
          <FileText size={50} />
        </div>
        <div className="absolute bottom-32 left-20 text-cyan-300 opacity-20">
          <TrendingUp size={35} />
        </div>
        <div className="absolute bottom-20 right-40 text-white opacity-10">
          <Shield size={45} />
        </div>

        <div className="container mx-auto px-4 pt-10 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {/* Left Content */}
            <div className="flex-1 text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1 mb-4 backdrop-blur">
                <Globe2 size={16} />
                <span className="text-xs font-semibold uppercase tracking-wide">
                  India-Based · UAE-Focused
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                UAE Company Formation,{" "}
                <span className="text-cyan-200">VAT</span> & Corporate Tax
                Compliance
              </h1>
              
              <div className="flex items-center gap-3 mt-8">
                <div className="bg-white rounded-lg p-2 flex items-center gap-1">
                  <span className="text-blue-600 font-bold text-2xl">G</span>
                  <span className="text-red-500 font-bold text-2xl">o</span>
                  <span className="text-yellow-500 font-bold text-2xl">o</span>
                  <span className="text-blue-600 font-bold text-2xl">g</span>
                  <span className="text-green-500 font-bold text-2xl">l</span>
                  <span className="text-red-500 font-bold text-2xl">e</span>
                </div>
                <div>
                  <div className="text-3xl font-bold">4.7/5</div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} size={16} fill="#FCD34D" stroke="#FCD34D" />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              variants={{
                hidden: { opacity: 0, y: 60 },
                show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } }
              }}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="flex-1 hidden lg:block"
            >
              <img
                src="/banner-img.png"
                alt="Banner"
                className="h-auto object-cover"
              />
            </motion.div>

            {/* Right Form */}
            <div className="w-full lg:w-96 py-10">
              <div className="bg-gradient-to-r from-orange-500 to-orange-700 rounded-2xl shadow-2xl p-8">

                {/* Heading */}
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-white mb-1">India-Based Team</h2>
                  <p className="text-cyan-100 text-sm">UAE-Focused Execution</p>
                </div>

                {/* Points */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-green-300 mt-1" />
                    <p className="text-white text-sm">
                      Registered CA / tax professionals with UAE-focused experience.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-green-300 mt-1" />
                    <p className="text-white text-sm">
                      Tie-ups with UAE-based consultants & free zone partners.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-green-300 mt-1" />
                    <p className="text-white text-sm">
                      Remote, document-driven process — minimal travel required.
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="space-y-3">
                  <a href='tel:+919315265493' className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-3 rounded-lg shadow-lg transition flex items-center justify-center gap-2">
                    Book a Free Consultation
                  </a >

                  <button className="w-full bg-white/20 hover:bg-white/30 text-white font-semibold py-3 rounded-lg shadow-lg transition flex items-center justify-center gap-2">
                    <FileText size={18} />
                    Download UAE Setup Checklist (PDF)
                  </button>
                </div>

                {/* Privacy */}
                <div className="flex items-center justify-center gap-2 text-white/80 text-xs mt-4">
                  <Shield size={14} className="text-white/80" />
                  <span>100% Privacy Guaranteed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Service Tags */}
        <div className="bg-blue-900 bg-opacity-50 border-t border-blue-700">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-wrap justify-center items-center gap-4 text-white text-sm">
              <span className="inline-flex items-center gap-1">
                <Building2 size={16} /> UAE Company Formation (Mainland / Free Zone / Offshore)
              </span>
              <span className="text-cyan-300">|</span>
              <span className="inline-flex items-center gap-1">
                <FileCheck2 size={16} /> VAT Registration & FTA Return
              </span>
              <span className="text-cyan-300">|</span>
              <span className="inline-flex items-center gap-1">
                <Calculator size={16} /> Corporate Tax Registration & Returns
              </span>
              <span className="text-cyan-300">|</span>
              <span className="inline-flex items-center gap-1">
                <TrendingUp size={16} /> Compliance & Risk Advisory
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white py-10">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-4">
            The Benefits Of Owning 100% Of Your UAE Business
          </h2>
          <p className="text-center text-gray-600 max-w-4xl mx-auto mb-16 leading-relaxed">
            Having full ownership of your company means having the final say in every decision and enjoying all the financial rewards. It's the perfect foundation for growth, innovation, and long-term success in the UAE. Partner with PreviewTax to make it happen.
          </p>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
            {/* Complete Control */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Control</h3>
              <p className="text-gray-600 text-sm">
                No need for a local partner. You have full control over your business decisions and profits.
              </p>
            </div>

            {/* Global Market Access */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Global Market Access</h3>
              <p className="text-gray-600 text-sm">
                Enjoy easy access to regional and international markets via world-class infrastructure and trade networks.
              </p>
            </div>

            {/* Tax-Friendly Environment */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Tax-Friendly Environment</h3>
              <p className="text-gray-600 text-sm">
                Enjoy 0% personal income tax and benefit from competitive corporate tax rates, with exemptions and incentives available in many Free Zones and for qualifying small businesses.
              </p>
            </div>

            {/* Easy Profit Repatriation */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Easy Profit Repatriation</h3>
              <p className="text-gray-600 text-sm">
                Send your profits back to your home country without restrictions or heavy taxation.
              </p>
            </div>

            {/* Business-Friendly Ecosystem */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Business-Friendly Ecosystem</h3>
              <p className="text-gray-600 text-sm">
                Quick licensing, flexible visa policies, and strong government support for startups and SMEs.
              </p>
            </div>

            {/* Residency Benefits */}
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Residency Benefits</h3>
              <p className="text-gray-600 text-sm">
                Get UAE residency visas for you and your family along with a business setup.
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a href="tel:+919315265493" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-4 rounded-lg transition-colors duration-200 shadow-lg text-lg">
              Schedule A Free Consultation
            </a>
          </div>
        </div>
      </div>

      {/* Expert Services Section */}
      <div className="bg-white py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left Content */}
            <div className="flex-1">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Your Trusted Partner for UAE Company Incorporation & Tax Compliance
              </h2>
              <div className="grid lg:grid-cols-2 gap-10">
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="inline-flex items-center justify-center rounded-full bg-orange-50 text-orange-600 p-3">
                      <Building2 size={22} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      UAE Company Incorporation
                    </h3>
                  </div>
                </div>
    
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="inline-flex items-center justify-center rounded-full bg-blue-50 text-blue-600 p-3">
                      <FileCheck2 size={22} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      VAT Registration & Compliance
                    </h3>
                  </div>
                </div>
    
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="inline-flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 p-3">
                      <Calculator size={22} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      UAE Corporate Tax Compliance
                    </h3>
                  </div>
                </div>
    
                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="inline-flex items-center justify-center rounded-full bg-cyan-50 text-cyan-600 p-3">
                      <TrendingUp size={22} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Accounting, Bookkeeping & MIS
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="flex-1">
              <div className="relative">
                <img 
                  src="/cta-grp.png" 
                  alt="Professional accountant" 
                  className="w-full max-w-lg mx-auto"
                />
              </div>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 mt-0">
            <div className="bg-orange-600 text-white p-8 text-center">
              <div className="flex justify-center">
                <img src="/business.svg" alt="img1" width="60" height="60" viewBox="0 0 24 24" fill="white" />
              </div>
              <p>Choose Business Activity</p>
            </div>
            
            <div className="bg-orange-600 text-white p-8 text-center">
              <div className="flex justify-center">
                <img src="/file.svg" alt="img2" width="60" height="60" viewBox="0 0 24 24" fill="white" />
              </div>
              <p>Select Jurisdiction</p>
            </div>
            
            <div className="bg-orange-600 text-white p-8 text-center">
              <div className="flex justify-center">
                <img src="/license.svg" alt="img3" width="60" height="60" viewBox="0 0 24 24" fill="white" />
              </div>
              <p>Get License</p>
            </div>
            
            <div className="bg-orange-600 text-white p-8 text-center">
              <div className="flex justify-center">
                <img src="/bank.svg" alt="img4" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </div>
              <p>Open Bank Account</p>
            </div>
          </div>
        </div>
      </div>

      {/* Business Setup Cards Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Mainland Card */}
            <div className="group h-[500px] perspective-1000">
              <div className="relative w-full h-full transition-transform duration-700 transform-style-3d group-hover:rotate-y-180">
                {/* Front */}
                <div className="absolute w-full h-full backface-hidden rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&h=600&fit=crop" 
                    alt="Dubai Mainland" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                    <div className="p-8 text-white">
                      <h3 className="text-3xl font-bold mb-2">Mainland</h3>
                      <p className="text-xl font-semibold">Business Setup</p>
                    </div>
                  </div>
                </div>
                
                {/* Back */}
                <div className="absolute w-full h-full backface-hidden rotate-y-180 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-orange-500 to-orange-800 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Dubai Mainland Company Setup</h3>
                  <p className="text-sm mb-4 leading-relaxed">
                    Operate your business from any location in the UAE and around the world with no restrictions.
                  </p>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>No fees for a local UAE sponsor</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>No requirement for paid-up share capital</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Free P.O. Box number in Dubai</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Over 1,000 professional activities to choose from</span>
                    </li>
                  </ul>
                  <button className="border-2 border-white text-white px-6 py-2 rounded-full hover:bg-white hover:text-black transition-colors duration-200">
                    Enquire Now
                  </button>
                </div>
              </div>
            </div>

            {/* Free Zone Card */}
            <div className="group h-[500px] perspective-1000">
              <div className="relative w-full h-full transition-transform duration-700 transform-style-3d group-hover:rotate-y-180">
                {/* Front */}
                <div className="absolute w-full h-full backface-hidden rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1582672060674-bc2bd808a8b5?w=800&h=600&fit=crop" 
                    alt="Dubai Free Zone" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                    <div className="p-8 text-white">
                      <h3 className="text-3xl font-bold mb-2">Free Zone</h3>
                      <p className="text-xl font-semibold">Business Setup</p>
                    </div>
                  </div>
                </div>
                
                {/* Back */}
                <div className="absolute w-full h-full backface-hidden rotate-y-180 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-orange-500 to-orange-800 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Free Zone Company Setup</h3>
                  <p className="text-sm mb-4 leading-relaxed">
                    Start a business in one of the 50+ free zones across the UAE, enjoying complete ownership and zero income tax.
                  </p>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>100% foreign ownership in Freezone companies without needing a local sponsor</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Multiple shareholders</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Tax exemptions and incentives</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Dedicated relationship manager</span>
                    </li>
                  </ul>
                  <button className="border-2 border-white text-white px-6 py-2 rounded-full hover:bg-white hover:text-black transition-colors duration-200">
                    Enquire Now
                  </button>
                </div>
              </div>
            </div>

            {/* Offshore Card */}
            <div className="group h-[500px] perspective-1000">
              <div className="relative w-full h-full transition-transform duration-700 transform-style-3d group-hover:rotate-y-180">
                {/* Front */}
                <div className="absolute w-full h-full backface-hidden rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/dubai-offshore.jpg"
                    alt="Dubai Offshore" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                    <div className="p-8 text-white">
                      <h3 className="text-3xl font-bold mb-2">Offshore</h3>
                      <p className="text-xl font-semibold">Business Setup</p>
                    </div>
                  </div>
                </div>
                
                {/* Back */}
                <div className="absolute w-full h-full backface-hidden rotate-y-180 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-orange-500 to-orange-800 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Offshore Company Setup</h3>
                  <p className="text-sm mb-4 leading-relaxed">
                    Set up your offshore company and operate outside the UAE with a corporate bank account.
                  </p>
                  <ul className="space-y-2 text-sm mb-6">
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>100% Foreign Ownership</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>100% Import and Export Tax Exemption</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>100% Repatriation of Capital and Profit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Abundant and Inexpensive Energy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>Personal Income Tax Exemption</span>
                    </li>
                  </ul>
                  <button className="border-2 border-white text-white px-6 py-2 rounded-full hover:bg-white hover:text-black transition-colors duration-200">
                    Enquire Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative bg-white py-0 pt-20 pb-0">
        <div 
          className="bg-cover bg-center relative"
          style={{ backgroundImage: `url('/hero-bg.png')` }}
        >
          <div className="flex flex-col-reverse lg:flex-row flex-col items-center justify-between gap-8 pt-10 px-6">
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 60 },
                show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } }
              }}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="flex-1 flex justify-center lg:justify-start"
            >
              <img 
                src="/cta-men.png"
                alt="Professional consultant"
                className="w-full max-w-md lg:-mt-28 md:-mt-28 select-none"
              />
            </motion.div>

            <div className="flex-1 text-white text-center lg:text-left pb-20">
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                Book a Free! <br />
                Consultation Today
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center lg:justify-start mt-4">
                <a href='tel:+919315265493' className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-8 py-3 rounded-lg shadow-lg transition">
                  Get Started
                </a>
                <span className="text-white font-semibold">Or</span>
                <a href="tel:+919315265493" className="bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-3 rounded-lg shadow-lg transition">
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>

      <style jsx>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        .group:hover .group-hover\\:rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </div>
  );
}